import React, { useEffect, useState } from "react";
import { Briefcase, Plus, Loader2, MapPin, Send, X, Users, Search as SearchIcon, MessageSquare, Check, Star } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

const EMPTY = { title: "", description: "", location: "", remote: false, comp: "", skills: "", category: "professional" };

export default function JobBoard() {
  const { user, setUser } = useApp();
  const [tab, setTab] = useState("browse");
  const [jobs, setJobs] = useState([]);
  const [applyCost, setApplyCost] = useState(5);
  const [q, setQ] = useState("");
  const [mine, setMine] = useState([]);
  const [apps, setApps] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [posting, setPosting] = useState(false);
  const [applyFor, setApplyFor] = useState(null);
  const [cover, setCover] = useState("");
  const [applying, setApplying] = useState(false);
  const [applicantsFor, setApplicantsFor] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [msgFor, setMsgFor] = useState(null); // { appId, title, withName }
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [msgLoading, setMsgLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const loadBrowse = () => api.get("/jobs", { params: { q } }).then(({ data }) => { setJobs(data.jobs); setApplyCost(data.apply_cost); }).catch(() => {});
  const loadMine = () => api.get("/jobs/mine").then(({ data }) => setMine(data.jobs)).catch(() => {});
  const loadApps = () => api.get("/jobs/applications/mine").then(({ data }) => setApps(data.applications)).catch(() => {});
  useEffect(() => { loadBrowse(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (tab === "mine") loadMine(); if (tab === "apps") loadApps(); /* eslint-disable-next-line */ }, [tab]);

  const post = async () => {
    if (!form.title.trim()) { toast.error("Add a job title."); return; }
    setPosting(true);
    try { await api.post("/jobs", form); toast.success("Job posted!"); setForm(EMPTY); setTab("mine"); loadMine(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setPosting(false); }
  };
  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(`/jobs/${applyFor.id}/apply`, { cover });
      toast.success(`Applied! ${data.charged} credits spent.`);
      if (setUser) setUser((u) => (u ? { ...u, credits: data.credits } : u));
      setApplyFor(null); setCover(""); loadBrowse();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setApplying(false); }
  };
  const viewApplicants = async (job) => {
    setApplicantsFor(job);
    try { const { data } = await api.get(`/jobs/${job.id}/applicants`); setApplicants(data.applicants); } catch { setApplicants([]); }
  };
  const setStatus = async (a, status) => {
    try {
      await api.post(`/jobs/applications/${a.id}/status`, { status });
      setApplicants((list) => list.map((x) => (x.id === a.id ? { ...x, status } : x)));
      toast.success(status === "shortlisted" ? `${a.applicant_name} shortlisted` : status === "rejected" ? "Applicant declined" : "Status updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openThread = async (appId, title, withName) => {
    setMsgFor({ appId, title, withName });
    setMessages([]); setMsgLoading(true);
    try { const { data } = await api.get(`/jobs/applications/${appId}/messages`); setMessages(data.messages); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setMsgLoading(false); }
  };
  const sendMsg = async () => {
    if (!msgText.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/jobs/applications/${msgFor.appId}/messages`, { text: msgText });
      setMessages((m) => [...m, data.message]); setMsgText("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSending(false); }
  };
  const closeJob = async (job) => {
    try { await api.delete(`/jobs/${job.id}`); toast.success("Job closed."); loadMine(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const JobCard = ({ j, own }) => (
    <div className="rounded-xl border border-rud bg-elevated p-4" data-testid={`job-${j.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{j.title}</div>
          <div className="text-[11px] text-sec flex flex-wrap items-center gap-x-2">
            <span>{j.org_name}</span>
            {j.location && <span className="flex items-center gap-0.5"><MapPin size={10} /> {j.location}</span>}
            {j.remote && <span className="text-brand">Remote</span>}
            {j.comp && <span>{j.comp}</span>}
          </div>
        </div>
        {j.status === "closed" && <span className="text-[10px] text-sec border border-rud rounded-full px-2 py-0.5">Closed</span>}
      </div>
      {j.description && <p className="text-xs text-sec mt-2 line-clamp-3">{j.description}</p>}
      {j.skills?.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{j.skills.slice(0, 8).map((s, i) => <span key={i} className="text-[10px] rounded-full bg-brand/10 text-brand px-2 py-0.5">{s}</span>)}</div>}
      <div className="flex items-center gap-2 mt-3">
        {own ? (
          <>
            <button data-testid={`job-applicants-${j.id}`} onClick={() => viewApplicants(j)} className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1.5"><Users size={13} /> {j.applicants_count || 0} applicants</button>
            {j.status === "open" && <button onClick={() => closeJob(j)} className="text-[11px] text-sec hover:text-red-400">Close</button>}
          </>
        ) : (
          <button data-testid={`job-apply-${j.id}`} onClick={() => setApplyFor(j)} className="btn-primary h-8 px-4 rounded-full text-xs flex items-center gap-1.5"><Send size={13} /> Apply · {applyCost} cr</button>
        )}
      </div>
    </div>
  );

  return (
    <div className="rud-card rounded-2xl p-6 mt-6" data-testid="job-board">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Briefcase size={14} /> Job Board</div>
        <div className="flex items-center gap-1 p-0.5 rounded-full bg-elevated border border-rud text-[11px]">
          {[["browse", "Browse"], ["post", "Post a job"], ["mine", "My postings"], ["apps", "My applications"]].map(([k, lbl]) => (
            <button key={k} data-testid={`jobtab-${k}`} onClick={() => setTab(k)} className={`px-3 py-1 rounded-full transition-colors ${tab === k ? "bg-brand text-black" : "text-sec hover:text-brand"}`}>{lbl}</button>
          ))}
        </div>
      </div>

      {tab === "browse" && (
        <>
          <div className="relative mb-3">
            <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-sec" />
            <input data-testid="job-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadBrowse()} placeholder="Search jobs…" className="rud-input w-full rounded-full pl-9 pr-3 py-2 text-sm" />
          </div>
          {jobs.length === 0 ? <p className="text-sm text-sec">No open jobs yet. Post the first one!</p> : (
            <div className="grid sm:grid-cols-2 gap-3">{jobs.map((j) => <JobCard key={j.id} j={j} />)}</div>
          )}
        </>
      )}

      {tab === "post" && (
        <div className="max-w-2xl">
          <input data-testid="job-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Job title (e.g. Senior AI Engineer)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mb-2" />
          <textarea data-testid="job-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} placeholder="Role, responsibilities, requirements…" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mb-2" />
          <div className="grid sm:grid-cols-2 gap-2">
            <input data-testid="job-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location" className="rud-input rounded-lg px-3 py-2.5 text-sm" />
            <input data-testid="job-comp" value={form.comp} onChange={(e) => setForm({ ...form, comp: e.target.value })} placeholder="Compensation (optional)" className="rud-input rounded-lg px-3 py-2.5 text-sm" />
          </div>
          <input data-testid="job-skills" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="Skills (comma separated)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-2" />
          <label className="flex items-center gap-2 text-sm mt-3"><input data-testid="job-remote" type="checkbox" checked={form.remote} onChange={(e) => setForm({ ...form, remote: e.target.checked })} /> Remote-friendly</label>
          <button data-testid="job-post-submit" onClick={post} disabled={posting} className="btn-primary h-11 px-6 mt-4 flex items-center gap-2 disabled:opacity-60">{posting ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Post job</button>
        </div>
      )}

      {tab === "mine" && (mine.length === 0 ? <p className="text-sm text-sec">You haven't posted any jobs yet.</p> : <div className="grid sm:grid-cols-2 gap-3">{mine.map((j) => <JobCard key={j.id} j={j} own />)}</div>)}

      {tab === "apps" && (apps.length === 0 ? <p className="text-sm text-sec">You haven't applied to any jobs yet.</p> : (
        <div className="space-y-2">{apps.map((a) => (
          <div key={a.id} className="flex items-center gap-3 rounded-lg border border-rud px-3 py-2 text-sm" data-testid={`myapp-${a.id}`}>
            <Briefcase size={14} className="text-brand shrink-0" />
            <span className="flex-1 truncate">{a.job?.title || "Job"} <span className="text-sec text-xs">· {a.job?.org_name}</span></span>
            <span className={`text-[11px] capitalize ${a.status === "shortlisted" ? "text-emerald-400" : a.status === "rejected" ? "text-red-400" : "text-brand"}`}>{a.status}</span>
            <button data-testid={`myapp-message-${a.id}`} onClick={() => openThread(a.id, a.job?.title || "Job", a.job?.org_name || "the organization")} className="btn-ghost rounded-full h-7 px-2.5 text-[11px] flex items-center gap-1"><MessageSquare size={12} /> Message</button>
          </div>
        ))}</div>
      ))}

      {applyFor && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setApplyFor(null)} data-testid="job-apply-modal">
          <div className="rud-card rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between"><h3 className="font-serif text-xl">Apply · {applyFor.title}</h3><button onClick={() => setApplyFor(null)} className="text-sec hover:text-brand"><X size={20} /></button></div>
            <p className="text-xs text-sec mt-1">{applyFor.org_name} · applying costs {applyCost} credits.</p>
            <textarea data-testid="job-cover" value={cover} onChange={(e) => setCover(e.target.value)} rows={5} placeholder="A short note to the organization (optional)…" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mt-3" />
            <button data-testid="job-apply-submit" onClick={apply} disabled={applying} className="btn-primary h-11 px-6 mt-3 flex items-center gap-2 disabled:opacity-60">{applying ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Apply · {applyCost} cr</button>
          </div>
        </div>
      )}

      {applicantsFor && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setApplicantsFor(null)} data-testid="job-applicants-modal">
          <div className="rud-card rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between"><h3 className="font-serif text-xl">Applicants · {applicantsFor.title}</h3><button onClick={() => setApplicantsFor(null)} className="text-sec hover:text-brand"><X size={20} /></button></div>
            {applicants.length === 0 ? <p className="text-sm text-sec mt-3">No applicants yet.</p> : (
              <div className="space-y-2 mt-3">{applicants.map((a) => (
                <div key={a.id} className="rounded-lg border border-rud p-3" data-testid={`applicant-${a.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium flex items-center gap-2">{a.applicant_name}
                      {a.status === "shortlisted" && <span className="text-[10px] text-emerald-400 border border-emerald-400/40 rounded-full px-2 py-0.5 flex items-center gap-0.5"><Star size={10} /> Shortlisted</span>}
                      {a.status === "rejected" && <span className="text-[10px] text-red-400 border border-red-400/40 rounded-full px-2 py-0.5">Declined</span>}
                    </div>
                  </div>
                  {a.cover && <p className="text-xs text-sec mt-1">{a.cover}</p>}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <button data-testid={`shortlist-${a.id}`} onClick={() => setStatus(a, "shortlisted")} disabled={a.status === "shortlisted"} className="btn-primary rounded-full h-7 px-3 text-[11px] flex items-center gap-1 disabled:opacity-50"><Star size={12} /> Shortlist</button>
                    <button data-testid={`message-applicant-${a.id}`} onClick={() => openThread(a.id, applicantsFor.title, a.applicant_name)} className="btn-ghost rounded-full h-7 px-3 text-[11px] flex items-center gap-1"><MessageSquare size={12} /> Message</button>
                    <button data-testid={`reject-${a.id}`} onClick={() => setStatus(a, "rejected")} className="text-[11px] text-sec hover:text-red-400 ml-auto">Decline</button>
                  </div>
                </div>
              ))}</div>
            )}
          </div>
        </div>
      )}

      {msgFor && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setMsgFor(null)} data-testid="job-message-modal">
          <div className="rud-card rounded-2xl p-6 max-w-lg w-full flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div><h3 className="font-serif text-xl">Messages</h3><p className="text-xs text-sec mt-0.5">{msgFor.title} · with {msgFor.withName}</p></div>
              <button onClick={() => setMsgFor(null)} className="text-sec hover:text-brand"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-auto my-3 space-y-2 min-h-[120px]" data-testid="job-message-list">
              {msgLoading ? <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-sec" /></div>
                : messages.length === 0 ? <p className="text-sm text-sec text-center py-6">No messages yet. Say hello 👋</p>
                : messages.map((m) => (
                  <div key={m.id} className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.sender_id === user?.user_id ? "ml-auto bg-brand text-black" : "bg-elevated border border-rud"}`}>
                    <div className="text-[10px] opacity-70 mb-0.5">{m.sender_name}</div>
                    {m.text}
                  </div>
                ))}
            </div>
            <div className="flex items-center gap-2">
              <input data-testid="job-message-input" value={msgText} onChange={(e) => setMsgText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder="Write a message…" className="rud-input flex-1 rounded-full px-4 py-2.5 text-sm" />
              <button data-testid="job-message-send" onClick={sendMsg} disabled={sending || !msgText.trim()} className="btn-primary rounded-full h-10 w-10 flex items-center justify-center shrink-0 disabled:opacity-50">{sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
